import { Button, TextField, WithStyles } from '@material-ui/core';
import { createStyles, withStyles } from '@material-ui/core/styles';
import React from 'react';
import Select from 'react-select';
import translations from '../../translations/i18next';
import ReduxLanguage from '../ReduxLanguage';
import { IMKSRequestParamsDTO } from '../../repository/jago/model/output/IMKSRequestDTO';
import AbilityProvider from '../../services/PermissionService/AbilityProvider';
import { PERMISSIONS } from '../../services/PermissionService/PermissionConstants';
import Permission from '../Permission/Permission';
import { CancelOutlined } from '@material-ui/icons';
import { MKSRequestStatusOptions, MKSRequestTypeOptions } from '../MKS/MKSRequestsTable';

const styles = createStyles({
  container: {
    display: 'flex',
    flexDirection: 'row',
    padding: 10,
    alignItems: 'center',
  },
  logo: {
    margin: 5,
    marginBottom: 15,
    alignSelf: 'center',
    height: 45,
  },
  text: {
    margin: 0,
    marginBottom: 0,
  },
  innerHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 10,
    marginLeft: 10,
  },
  title: {
    color: 'white',
  },
  circularProgress: {
    color: 'white',
  },
});

export interface IStatusOption {
  label: string;
  value: string;
}

export interface IRequestTypeOption {
  label: string;
  value: string;
}

export interface IMKSRequestSearchBarProps extends WithStyles<typeof styles> {
  onSearch: (searchValues: IMKSRequestParamsDTO) => void;
  onResetSearch: () => void;
  totalElements: string | number;
  onCreateNewEntityButtonClick: () => void;
}

export interface IMKSRequestSearchBarState {
  searchValues: IMKSRequestParamsDTO;
  statusSelected: IStatusOption | null;
  requestTypeSelected: IRequestTypeOption | null;
}

class MKSRequestSearchBar extends React.Component<IMKSRequestSearchBarProps, IMKSRequestSearchBarState> {

  constructor(props: IMKSRequestSearchBarProps) {
    super(props);
    this.state = {
      searchValues: {
        search: "",
        requestType: undefined,
        status: undefined,
      },
      statusSelected: null,
      requestTypeSelected: null,
    }
  }

  public onSetSearchValue(field: keyof IMKSRequestParamsDTO, value: any) {
    const { searchValues } = this.state;
    (searchValues as any)[field] = value;
    this.setState({ searchValues });
    this.onSearch()
  }

  public onSelectStatus(status: IStatusOption | null) {
    this.onSetSearchValue('status', status?.value);
    this.setState({ statusSelected: status });
  }

  public onSelectRequestType(requestType: IRequestTypeOption | null) {
    this.onSetSearchValue('requestType', requestType?.value);
    this.setState({ requestTypeSelected: requestType });
  }

  public onSearch() {
    const { onSearch } = this.props;
    onSearch(this.state.searchValues);
  }

  public onResetFields() {
    const { onResetSearch } = this.props;
    this.setState({
      searchValues: {
        search: "",
        requestType: undefined,
        status: undefined,
      },
      statusSelected: null,
      requestTypeSelected: null,
    });
    onResetSearch();
  }

  public render() {
    const { classes, onCreateNewEntityButtonClick, totalElements } = this.props;
    const { searchValues, statusSelected, requestTypeSelected } = this.state;

    const statusOptions: IStatusOption[] = Object.keys(MKSRequestStatusOptions)
      .filter(key => key !== 'CANCELED') // Escludi CANCELED dal filtro
      .map(key => ({
        label: translations.t(`mks.requestStatus_${key}`),
        value: key,
      }));

    const requestTypeOptions: IRequestTypeOption[] = Object.keys(MKSRequestTypeOptions).map(key => ({
      label: translations.t(`mks.requestType_${key}`),
      value: key,
    }));

    return (
      <div className={classes.container}>
        <TextField
          id="outlined-basic"
          label={<ReduxLanguage languageKey="forms.search" />}
          variant="outlined"
          type="search"
          style={{ marginLeft: 0, marginRight: 10 }}
          value={searchValues.search}
          onChange={e => this.onSetSearchValue('search', e.target.value)}
        />
        <Select
          key={`${JSON.stringify(requestTypeSelected ? requestTypeSelected : -1)}`}
          isClearable
          value={requestTypeSelected}
          options={requestTypeOptions}
          placeholder={<><ReduxLanguage languageKey={'mks.requestTypeFilter'} /></>}
          formatOptionLabel={(o) => <>{o.label}</>}
          getOptionLabel={(o) => `${o.label}`}
          isOptionSelected={(o) => requestTypeSelected?.value === o.value}
          onChange={(e) => this.onSelectRequestType(e)}
          styles={{
            menu: (styles) => Object.assign(styles, { zIndex: 1000 }),
            control: (styles) => ({ ...styles, minHeight: 55, minWidth: 200, marginLeft: 10, marginRight: 10, maxWidth: 200 }),
          }}
        />
        <Select
          key={`${statusSelected?.value || 'none'}`}
          isClearable
          value={statusSelected}
          options={statusOptions}
          placeholder={<><ReduxLanguage languageKey={'mks.statusFilter'} /></>}
          formatOptionLabel={(o) => <>{o.label}</>}
          getOptionLabel={(o) => `${o.label}`}
          onChange={(e) => this.onSelectStatus(e as IStatusOption | null)}
          styles={{
            menu: (styles) => Object.assign(styles, { zIndex: 1000 }),
            control: (styles) => ({ ...styles, minHeight: 55, minWidth: 250, marginLeft: 10, marginRight: 10, maxWidth: 250 }),
          }}
        />
        <Button
          variant="contained"
          style={{ backgroundColor: 'red', height: 40, color: 'white', fontWeight: 'bold' }}
          onClick={() => this.onResetFields()}
        >
          <CancelOutlined style={{ color: 'white' }} />
        </Button>
        <span style={{ flexGrow: '1' }}></span>
        <Permission abilityHelper={AbilityProvider.getAbilityHelper()} permission={PERMISSIONS.MKS_CREATE_REQUEST}>
          <Button
            variant="contained"
            style={{ backgroundColor: '#5AC0B1', color: 'white', marginRight: 20, marginLeft: 40, fontWeight: 'bold' }}
            onClick={() => onCreateNewEntityButtonClick()}
          >
            <ReduxLanguage languageKey="forms.add" />
          </Button>
        </Permission>
      </div>
    );
  }
}

export default withStyles(styles)(MKSRequestSearchBar);
